import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Box,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import StarIcon from '@mui/icons-material/Star';
import { Place } from '@/types/place';
import { useUiStore } from '@/stores/uiStore';
import FavoritesComponent from './FavoritesComponent';
import { useAuth } from '@/contexts/AuthContext';

interface PlaceOverlayCardProps {
  place: Place & { coordinates?: { latitude: number; longitude: number } };
  onClose?: () => void;
  favorites?: Place[];
}

/**
 * Overlay card component for displaying place information on map.
 * Reuses PlaceCard styling patterns for consistency.
 */
export const PlaceOverlayCard: React.FC<PlaceOverlayCardProps> = ({ place, onClose, favorites }) => {
  const navigate = useNavigate();
  const { isAuthenticated, userId, authToken } = useAuth();
  const setSelectedPlaceId = useUiStore((state) => state.setSelectedPlaceId);

  // Calculate average rating from place.ratings array or use place.rating
  const averageRating = (place as any).ratings && (place as any).ratings.length > 0
    ? (place as any).ratings.reduce((sum: number, r: any) => sum + (r.rating || 0), 0) / (place as any).ratings.length
    : place.rating || 0;

  // Format address: address, city, country
  const addressParts = [];
  if ((place as any).address) addressParts.push((place as any).address);
  if (place.city) addressParts.push(place.city);
  if (place.country) addressParts.push(place.country);
  const address = addressParts.length > 0 ? addressParts.join(', ') : place.country || '';

  // Truncate description to 2-3 lines (approximately 150 characters)
  const maxDescriptionLength = 150;
  const truncatedDescription = place.description && place.description.length > maxDescriptionLength
    ? `${place.description.substring(0, maxDescriptionLength)}...`
    : place.description || '';

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling to map
    navigate(`/places/${place._id}`);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setSelectedPlaceId(null);
    }
  };

  // Get image - first image from images array
  const imageUrl = Array.isArray(place.images)
    ? place.images[0]
    : place.images || '';

  return (
    <Card
      sx={{
        borderRadius: 1, // 8px border radius (theme.spacing(1))
        boxShadow: 1, // Subtle shadow (theme.shadows[1])
        maxWidth: { xs: '90vw', md: '280px' }, // Responsive width - reduced for better visibility
        width: '100%',
        cursor: 'default',
        '&:hover': {
          boxShadow: 4, // Elevated shadow on hover (theme.shadows[4])
        },
      }}
      onClick={(e) => e.stopPropagation()} // Prevent map click from closing card
    >
      {/* Image */}
      {imageUrl && (
        <CardMedia
          component="img"
          image={imageUrl}
          alt={place.name}
          loading="lazy"
          sx={{
            objectFit: 'cover',
            aspectRatio: '16/9', // Fixed aspect ratio
            width: '100%',
          }}
        />
      )}

      {/* Content */}
      <CardContent sx={{ p: 3 }}>
        {/* Name with Heart Icon */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" component="div" sx={{ flex: 1 }}>
            {place.name}
          </Typography>
          {isAuthenticated && (
            <FavoritesComponent
              placeId={place._id}
              refetchPlaceData={() => {}}
              isAuthenticated={isAuthenticated}
              userId={userId}
              authToken={authToken}
              favorites={favorites}
            />
          )}
        </Box>

        {/* Rating and Address */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          {averageRating > 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StarIcon sx={{ fontSize: 18, color: 'accent.main' }} />
              <Typography variant="body2" color="text.secondary">
                {averageRating.toFixed(1)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Not rated
            </Typography>
          )}
          {address && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                •
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {address}
              </Typography>
            </>
          )}
        </Box>

        {/* Description */}
        {truncatedDescription && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {truncatedDescription}
          </Typography>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            onClick={handleViewClick}
            variant="outlined"
            size="small"
            startIcon={<VisibilityOutlinedIcon />}
            aria-label="View place details"
          >
            View
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PlaceOverlayCard;
